import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Tag,
  Space,
  Popconfirm,
  message,
  Badge,
  Tooltip,
  Switch,
  Divider,
  Row,
  Col,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BellOutlined,
  PlayCircleOutlined,
  TrophyOutlined,
  CalendarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import axios from 'axios';
import LatexText from '../../components/LatexText';
import { govtAdminAPI } from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const API_URL = process.env.REACT_APP_API_URL || 'https://api.calloctech.com/api';

const EXAM_TYPES = ['KSP PC', 'KSP ESI', 'FDA SDA', 'PSI', 'Custom'];
const ENTRANCE_EXAM_TYPES = ['NEET', 'JEE', 'KCET', 'MHT_CET'];
const isGovtScheduledTest = (t: ScheduledTest) =>
  EXAM_TYPES.includes(t.exam_type) && !ENTRANCE_EXAM_TYPES.includes(t.exam_type);
const SUBJECTS   = ['General Knowledge', 'Karnataka History & Culture', 'Indian Polity', 'English', 'Mathematics', 'Reasoning', 'Computer Awareness', 'Science & Technology', 'All Subjects'];
const CLASS_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];
const TEST_TYPES = ['daily', 'weekly', 'monthly'];

const EXAM_MARKING: Record<string, { correct: number; wrong: number }> = {
  'KSP PC':  { correct: 1, wrong: 0 },
  'KSP ESI': { correct: 1, wrong: 0 },
  'FDA SDA': { correct: 1, wrong: 0 },
  'PSI':     { correct: 1, wrong: 0 },
  Custom:    { correct: 1, wrong: 0 },
};

const TYPE_COLOR: Record<string, string> = {
  daily: 'green', weekly: 'blue', monthly: 'purple',
};

interface ScheduledTest {
  id: string;
  title: string;
  description?: string;
  test_type: string;
  exam_type: string;
  subjects: string[];
  class_level: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  total_marks: number;
  marking_scheme: { correct: number; wrong: number };
  is_published: boolean;
  notify_students: boolean;
  question_count?: number;
  question_ids?: string[];
}

interface Question {
  id: string;
  question_text?: string;
  question?: string;
  subject: string;
  topic?: string;
  difficulty?: string;
}

const ScheduledTestsPage: React.FC = () => {
  const [tests, setTests]                       = useState<ScheduledTest[]>([]);
  const [loading, setLoading]                   = useState(false);
  const [modalOpen, setModalOpen]               = useState(false);
  const [editing, setEditing]                   = useState<ScheduledTest | null>(null);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [questions, setQuestions]               = useState<Question[]>([]);
  const [selectedQIds, setSelectedQIds]         = useState<string[]>([]);
  const [qSearch, setQSearch]                   = useState('');
  const [qLoading, setQLoading]                 = useState(false);
  const [notifyModalOpen, setNotifyModalOpen]   = useState(false);
  const [notifyTestId, setNotifyTestId]         = useState<string>('');
  const [notifyMsg, setNotifyMsg]               = useState('');
  const [notifySending, setNotifySending]       = useState(false);
  const [qFilters, setQFilters] = useState<{ subject?: string; difficulty?: string }>({});

  const [form] = Form.useForm();

  useEffect(() => { loadTests(); }, []);

  const getToken = async () => {
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      return await auth.currentUser?.getIdToken();
    } catch { return ''; }
  };

  const authHeaders = async () => ({
    Authorization: `Bearer ${await getToken()}`,
  });

  const loadTests = async () => {
    setLoading(true);
    try {
      const h = await authHeaders();
      const res = await axios.get(`${API_URL}/admin/scheduled-tests`, { headers: h });
      const raw = res.data.data ?? res.data ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setTests(list.filter(isGovtScheduledTest));
    } catch { message.error('Failed to load scheduled tests'); }
    finally { setLoading(false); }
  };

  const loadQuestions = async () => {
    setQLoading(true);
    try {
      // Backend only supports filtering by subjectId (UUID) and difficulty — the
      // scheduled-test "subject" filter here is a plain name, and free-text search
      // isn't supported server-side, so both are applied client-side below.
      const res = await govtAdminAPI.getQuestions({ difficulty: qFilters.difficulty, pageSize: 200 });
      let raw = res.data?.questions ?? res.data?.data ?? res.data ?? [];
      raw = Array.isArray(raw) ? raw : [];
      if (qFilters.subject) {
        raw = raw.filter((q: any) => q.subject_name === qFilters.subject);
      }
      if (qSearch.trim()) {
        const needle = qSearch.trim().toLowerCase();
        raw = raw.filter((q: any) => q.question?.toLowerCase().includes(needle));
      }
      setQuestions(raw);
    } catch { message.error('Failed to load questions'); }
    finally { setQLoading(false); }
  };

  useEffect(() => {
    if (questionModalOpen) loadQuestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionModalOpen, qFilters]);

  const openCreate = () => {
    setEditing(null);
    setSelectedQIds([]);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (test: ScheduledTest) => {
    setEditing(test);
    setSelectedQIds(test.question_ids ?? []);
    form.setFieldsValue({
      ...test,
      timeRange: [dayjs(test.start_time), dayjs(test.end_time)],
      subjects: test.subjects,
      marking_correct: test.marking_scheme.correct,
      marking_wrong:   test.marking_scheme.wrong,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const [start, end] = values.timeRange;
      const ms = EXAM_MARKING[values.exam_type] ?? EXAM_MARKING.Custom;
      const payload = {
        title:            values.title,
        description:      values.description,
        test_type:        values.test_type,
        exam_type:        values.exam_type,
        subjects:         values.subjects,
        class_level:      values.class_level,
        start_time:       start.toISOString(),
        end_time:         end.toISOString(),
        duration_minutes: values.duration_minutes,
        total_marks:      selectedQIds.length * ms.correct,
        marking_scheme:   { correct: ms.correct, wrong: ms.wrong },
        is_published:     values.is_published ?? false,
        notify_students:  values.notify_students ?? true,
        question_ids:     selectedQIds,
      };
      const h = await authHeaders();
      if (editing) {
        await axios.put(`${API_URL}/admin/scheduled-tests/${editing.id}`, payload, { headers: h });
        message.success('Test updated');
      } else {
        await axios.post(`${API_URL}/admin/scheduled-tests`, payload, { headers: h });
        message.success('Test created');
      }
      setModalOpen(false);
      loadTests();
    } catch (e: any) {
      if (e.errorFields) return; // form validation error
      message.error(e.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const h = await authHeaders();
      await axios.delete(`${API_URL}/admin/scheduled-tests/${id}`, { headers: h });
      message.success('Deleted');
      loadTests();
    } catch { message.error('Delete failed'); }
  };

  const handlePublish = async (id: string, val: boolean) => {
    try {
      const h = await authHeaders();
      await axios.post(`${API_URL}/admin/scheduled-tests/${id}/publish`, { publish: val }, { headers: h });
      message.success(val ? 'Published' : 'Unpublished');
      loadTests();
    } catch { message.error('Failed'); }
  };

  const sendNotification = async () => {
    setNotifySending(true);
    try {
      const h = await authHeaders();
      await axios.post(
        `${API_URL}/admin/scheduled-tests/${notifyTestId}/notify`,
        { message: notifyMsg },
        { headers: h },
      );
      message.success('Notification sent to all students!');
      setNotifyModalOpen(false);
      setNotifyMsg('');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Failed to send notification');
    } finally {
      setNotifySending(false);
    }
  };

  const getStatus = (test: ScheduledTest) => {
    const now = Date.now();
    const s   = new Date(test.start_time).getTime();
    const e   = new Date(test.end_time).getTime();
    if (!test.is_published) return <Tag color="default">Draft</Tag>;
    if (now < s)            return <Tag color="orange">Upcoming</Tag>;
    if (now > e)            return <Tag color="gray">Ended</Tag>;
    return <Badge status="processing" text={<Tag color="green">Live</Tag>} />;
  };

  const columns: ColumnsType<ScheduledTest> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (v, r) => (
        <div>
          <Text strong>{v}</Text>
          <div>
            <Tag color={TYPE_COLOR[r.test_type]}>{r.test_type.toUpperCase()}</Tag>
            <Tag color="blue">{r.exam_type}</Tag>
            {r.subjects.map(s => <Tag key={s}>{s}</Tag>)}
          </div>
        </div>
      ),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (_, r) => (
        <div>
          <div><CalendarOutlined /> {dayjs(r.start_time).format('DD MMM YYYY HH:mm')}</div>
          <div style={{ color: '#6B7280', fontSize: 12 }}>to {dayjs(r.end_time).format('DD MMM YYYY HH:mm')}</div>
          <div style={{ marginTop: 2 }}><Tag>{r.duration_minutes} min</Tag></div>
        </div>
      ),
    },
    {
      title: 'Questions / Marks',
      key: 'qmarks',
      render: (_, r) => (
        <div>
          <div>{r.question_count ?? (r.question_ids?.length ?? '–')} questions</div>
          <div>{r.total_marks} marks</div>
          <div style={{ color: '#6B7280', fontSize: 12 }}>+{r.marking_scheme.correct} / {r.marking_scheme.wrong}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => getStatus(r),
    },
    {
      title: 'Published',
      key: 'published',
      render: (_, r) => (
        <Switch
          checked={r.is_published}
          onChange={val => handlePublish(r.id, val)}
          checkedChildren="Live"
          unCheckedChildren="Draft"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(r)} />
          </Tooltip>
          <Tooltip title="Send Notification">
            <Button
              icon={<BellOutlined />}
              size="small"
              type="primary"
              ghost
              onClick={() => {
                setNotifyTestId(r.id);
                setNotifyMsg(`📅 ${r.test_type.charAt(0).toUpperCase() + r.test_type.slice(1)} ${r.exam_type} test is now live! Start your test before ${dayjs(r.end_time).format('DD MMM HH:mm')}.`);
                setNotifyModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="View Leaderboard">
            <Button
              icon={<TrophyOutlined />}
              size="small"
              href={`/scheduled-tests/${r.id}/leaderboard`}
              target="_blank"
            />
          </Tooltip>
          <Popconfirm title="Delete this test?" onConfirm={() => handleDelete(r.id)} okText="Delete" okType="danger">
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Question selection table columns
  const qColumns: ColumnsType<Question> = [
    {
      title: 'Question',
      key: 'q',
      render: (_, r) => (
        <div style={{ maxWidth: 500 }}>
          <LatexText text={(r.question || r.question_text || '').slice(0, 120) + '…'} />
        </div>
      ),
    },
    { title: 'Subject', dataIndex: 'subject', key: 'subject', width: 100 },
    { title: 'Topic', dataIndex: 'topic', key: 'topic', width: 150 },
    { title: 'Difficulty', dataIndex: 'difficulty', key: 'difficulty', width: 90,
      render: v => v ? <Tag color={v === 'hard' ? 'red' : v === 'medium' ? 'orange' : 'green'}>{v}</Tag> : '–' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Scheduled Tests</Title>
          <Text type="secondary">Create and manage daily, weekly & monthly govt exam tests (KSP, FDA, PSI)</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} size="large">
          Create Scheduled Test
        </Button>
      </div>

      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        {TEST_TYPES.map(t => {
          const count = tests.filter(x => x.test_type === t).length;
          const live  = tests.filter(x => x.test_type === t && x.is_published &&
            Date.now() >= new Date(x.start_time).getTime() && Date.now() <= new Date(x.end_time).getTime()).length;
          return (
            <Col key={t} xs={24} sm={8}>
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <Text type="secondary">{t.toUpperCase()} TESTS</Text>
                    <Title level={2} style={{ margin: 0, color: TYPE_COLOR[t] === 'green' ? '#10B981' : TYPE_COLOR[t] === 'blue' ? '#3B82F6' : '#8B5CF6' }}>{count}</Title>
                  </div>
                  {live > 0 && <Badge count={`${live} live`} color="green" />}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card>
        <Table
          dataSource={tests}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      {/* ── Create / Edit Modal ── */}
      <Modal
        open={modalOpen}
        title={editing ? 'Edit Scheduled Test' : 'Create Scheduled Test'}
        width={760}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>Cancel</Button>,
          <Button key="qs" onClick={() => setQuestionModalOpen(true)} icon={<SearchOutlined />}>
            Select Questions ({selectedQIds.length})
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            {editing ? 'Update' : 'Create'}
          </Button>,
        ]}>
        <Form form={form} layout="vertical" initialValues={{ is_published: false, notify_students: true, duration_minutes: 60, exam_type: 'KSP PC', test_type: 'daily', class_level: 'Intermediate' }}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="title" label="Test Title" rules={[{ required: true }]}>
                <Input placeholder="e.g. Daily NEET Physics – Day 1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="test_type" label="Test Type" rules={[{ required: true }]}>
                <Select>
                  {TEST_TYPES.map(t => <Select.Option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description or instructions" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="exam_type" label="Exam Type" rules={[{ required: true }]}>
                <Select>
                  {EXAM_TYPES.map(e => <Select.Option key={e} value={e}>{e}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="class_level" label="Level" rules={[{ required: true }]}>
                <Select>
                  {CLASS_LEVELS.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="duration_minutes" label="Duration (minutes)" rules={[{ required: true }]}>
                <InputNumber min={5} max={360} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="subjects" label="Subjects (select all that apply)" rules={[{ required: true, type: 'array', min: 1 }]}>
            <Select mode="multiple" placeholder="Select subjects">
              {SUBJECTS.map(s => <Select.Option key={s} value={s}>{s}</Select.Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="timeRange" label="Test Window (Start – End)" rules={[{ required: true }]}>
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.exam_type !== cur.exam_type}>
            {({ getFieldValue }) => {
              const et = getFieldValue('exam_type');
              const ms = EXAM_MARKING[et] ?? EXAM_MARKING.Custom;
              return (
                <Card size="small" style={{ backgroundColor: '#F0F9FF', marginBottom: 16 }}>
                  <Text>
                    <strong>{et} Marking Scheme:</strong> +{ms.correct} correct · {ms.wrong === 0 ? '0' : ms.wrong} wrong (no negative)
                    &nbsp;·&nbsp; {selectedQIds.length} questions selected
                    &nbsp;·&nbsp; <strong>Total: {selectedQIds.length * ms.correct} marks</strong>
                  </Text>
                </Card>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_published" label="Publish" valuePropName="checked">
                <Switch checkedChildren="Published (Live)" unCheckedChildren="Draft" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="notify_students" label="Notify Students" valuePropName="checked">
                <Switch checkedChildren="Yes, notify" unCheckedChildren="No notification" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Question Selection Modal ── */}
      <Modal
        open={questionModalOpen}
        title={`Select Questions (${selectedQIds.length} selected)`}
        width={900}
        onCancel={() => setQuestionModalOpen(false)}
        onOk={() => setQuestionModalOpen(false)}>
        <Space style={{ marginBottom: 12 }}>
          <Input.Search
            placeholder="Search questions"
            value={qSearch}
            onChange={e => setQSearch(e.target.value)}
            onSearch={loadQuestions}
            style={{ width: 240 }}
          />
          <Select
            placeholder="Subject"
            allowClear
            style={{ width: 140 }}
            onChange={v => setQFilters(f => ({ ...f, subject: v }))}>
            {SUBJECTS.map(s => <Select.Option key={s} value={s}>{s}</Select.Option>)}
          </Select>
          <Select
            placeholder="Difficulty"
            allowClear
            style={{ width: 130 }}
            onChange={v => setQFilters(f => ({ ...f, difficulty: v }))}>
            {['easy','medium','hard'].map(d => <Select.Option key={d} value={d}>{d}</Select.Option>)}
          </Select>
          <Button onClick={loadQuestions} icon={<SearchOutlined />}>Refresh</Button>
        </Space>

        <Table
          dataSource={questions}
          columns={qColumns}
          rowKey="id"
          loading={qLoading}
          rowSelection={{
            selectedRowKeys: selectedQIds,
            onChange: keys => setSelectedQIds(keys as string[]),
            preserveSelectedRowKeys: true,
          }}
          pagination={{ pageSize: 15 }}
          scroll={{ y: 400 }}
          size="small"
        />
        <div style={{ marginTop: 8, color: '#6B7280' }}>
          {selectedQIds.length} questions selected
        </div>
      </Modal>

      {/* ── Send Notification Modal ── */}
      <Modal
        open={notifyModalOpen}
        title={<><BellOutlined /> Send Push Notification</>}
        onCancel={() => setNotifyModalOpen(false)}
        onOk={sendNotification}
        okText="Send Now"
        confirmLoading={notifySending}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          This will send a push notification to all students via Firebase Cloud Messaging.
        </Text>
        <TextArea
          rows={4}
          value={notifyMsg}
          onChange={e => setNotifyMsg(e.target.value)}
          placeholder="Notification message…"
        />
      </Modal>
    </div>
  );
};

export default ScheduledTestsPage;
